import { action, computed, makeObservable, observable } from 'mobx'
import { v1 as createUuid } from 'uuid'

type TilesetUploadStatus =
  | 'uploading'
  | 'notifying-backend'
  | 'completed'
  | 'error'

export interface ITilesetUploadState {
  id: string
  fileName: string
  fileSize: number
  uploadedBytes: number
  status: TilesetUploadStatus
  error?: string
  startedAt: number
  tilesetId?: string
  abortController?: () => void
}

export default class TilesetUploadProgressStore {
  @observable public uploads: Map<string, ITilesetUploadState> = new Map()

  public constructor() {
    makeObservable(this)
  }

  @computed
  public get activeUploads(): ITilesetUploadState[] {
    return Array.from(this.uploads.values()).filter(
      u => u.status === 'uploading' || u.status === 'notifying-backend',
    )
  }

  @computed
  public get hasActiveUploads(): boolean {
    return this.activeUploads.length > 0
  }

  @computed
  public get failedUploads(): ITilesetUploadState[] {
    return Array.from(this.uploads.values()).filter(u => u.status === 'error')
  }

  @action.bound
  public startUpload(fileName: string, fileSize: number): string {
    const id = createUuid()
    this.uploads.set(id, {
      id,
      fileName,
      fileSize,
      uploadedBytes: 0,
      status: 'uploading',
      startedAt: Date.now(),
    })
    return id
  }

  @action.bound
  public updateProgress(id: string, uploadedBytes: number): void {
    const upload = this.uploads.get(id)
    if (upload) {
      upload.uploadedBytes = uploadedBytes
    }
  }

  @action.bound
  public setNotifyingBackend(id: string): void {
    const upload = this.uploads.get(id)
    if (upload) {
      upload.status = 'notifying-backend'
    }
  }

  @action.bound
  public completeUpload(id: string, tilesetId?: string): void {
    const upload = this.uploads.get(id)
    if (upload) {
      upload.status = 'completed'
      upload.tilesetId = tilesetId
    }
    // Auto-cleanup after 5 seconds
    setTimeout(() => this.removeUpload(id), 5000)
  }

  @action.bound
  public setError(id: string, error: string): void {
    const upload = this.uploads.get(id)
    if (upload) {
      upload.status = 'error'
      upload.error = error
    }
  }

  @action.bound
  public removeUpload(id: string): void {
    this.uploads.delete(id)
  }

  public registerAbortCallback(id: string, abortFn: () => void): void {
    const upload = this.uploads.get(id)
    if (upload) {
      upload.abortController = abortFn
    }
  }

  @action.bound
  public cancelUpload(id: string): void {
    const upload = this.uploads.get(id)
    if (upload?.abortController) {
      upload.abortController()
    }
    this.uploads.delete(id)
  }

  public getProgressPercentage(id: string): number {
    const upload = this.uploads.get(id)
    if (!upload || upload.fileSize === 0) return 0
    return Math.round((upload.uploadedBytes / upload.fileSize) * 100)
  }
}
